import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Minus, Plus, ArrowLeft, ShoppingBag, BellRing, PlayCircle, ImageDown } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/lib/AuthContext';
import { useCart } from '@/lib/CartContext';
import { IDS } from '@/constants/testIds';
import PriceBlock from '@/components/PriceBlock';
import ProductInfoTabs from '@/components/ProductInfoTabs';
import ProductDownloadsCard from '@/components/ProductDownloadsCard';
import AddToCatalogButton from '@/components/AddToCatalogButton';
import { toast } from 'sonner';

const FALLBACK_IMG = `data:image/svg+xml;utf8,${encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 600 600'><defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'><stop offset='0' stop-color='#FBF0F6'/><stop offset='1' stop-color='#C2478A'/></linearGradient></defs><rect width='600' height='600' fill='url(#g)'/><text x='300' y='320' text-anchor='middle' font-family='Georgia, serif' font-style='italic' font-size='140' fill='#0A0A0A' opacity='0.4'>IJE</text></svg>`)}`;

function NotifyForm({ product }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await api.post('/notify-me', { sku: product.sku, email, name, phone });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="border border-neutral-200 p-5 bg-[color:var(--ije-pink-tint)]/40" data-testid="notify-success">
        <p className="serif text-lg">We&rsquo;ll be in touch.</p>
        <p className="sans text-sm text-neutral-600 mt-1">You&rsquo;re on the list for <strong>{product.name}</strong>. Expect a note the moment it&rsquo;s back.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="border border-neutral-200 p-5" data-testid="notify-form">
      <div className="flex items-center gap-2 mb-3">
        <BellRing size={16} strokeWidth={1.5} style={{ color: 'var(--ije-pink)' }} />
        <p className="serif text-lg">Out of Stock — notify me</p>
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <input required type="email" data-testid="notify-email" placeholder="Email *" value={email} onChange={(e) => setEmail(e.target.value)} className="ije-input md:col-span-1" />
        <input data-testid="notify-name" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} className="ije-input" />
        <input data-testid="notify-phone" placeholder="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="ije-input" />
      </div>
      {error && <p className="sans text-sm mt-3" style={{ color: 'var(--ije-pink-dark)' }} data-testid="notify-error">{error}</p>}
      <button type="submit" disabled={busy} className="btn-primary mt-4" data-testid="notify-submit">
        {busy ? 'Submitting…' : 'Notify me'}
      </button>
    </form>
  );
}

export default function ProductDetail() {
  const params = useParams();
  const key = params.sku || params.id;
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refresh: refreshCart } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [adding, setAdding] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    if (!key) return;
    api.get(`/products/${encodeURIComponent(key)}`).then((res) => {
      setProduct(res.data);
      setQty(res.data.min_order_qty || 1);
      setActiveImage(0);
      setShowVideo(false);
    }).catch(() => navigate('/products'));
  }, [key, navigate]);

  const step = product?.case_pack_size || 1;
  const minQty = product?.min_order_qty || 1;

  const inc = () => setQty((q) => q + step);
  const dec = () => setQty((q) => Math.max(minQty, q - step));
  const setQtyManual = (val) => {
    const n = parseInt(val, 10);
    if (Number.isNaN(n)) return setQty(minQty);
    setQty(Math.max(minQty, n));
  };

  const downloadImagesZip = async () => {
    if (!product) return;
    try {
      const resp = await api.get(`/products/${encodeURIComponent(product.sku)}/images.zip`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${product.sku}-images.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch {
      toast.error('Could not download images');
    }
  };

  const addToCart = async () => {
    if (!user) return navigate('/login');
    if (user.status !== 'active' && user.role !== 'admin') {
      toast.error('Your account is still under review. Pricing and quotes unlock once approved.');
      return;
    }
    setAdding(true);
    try {
      const { data: cart } = await api.get('/cart');
      const existing = cart.items.find((i) => i.product_id === product.id);
      const items = existing
        ? cart.items.map((i) => (i.product_id === product.id ? { product_id: i.product_id, qty: i.qty + qty } : { product_id: i.product_id, qty: i.qty }))
        : [...cart.items.map((i) => ({ product_id: i.product_id, qty: i.qty })), { product_id: product.id, qty }];
      await api.put('/cart', { items });
      await refreshCart();
      toast.success(`Added ${qty} × ${product.name} to your cart`, {
        action: {
          label: 'View Cart',
          onClick: () => navigate('/cart'),
        },
        duration: 6000,
      });
    } catch (e) {
      toast.error('Could not add to cart');
    } finally {
      setAdding(false);
    }
  };

  if (!product) {
    return <div className="container-x py-24 text-center sans text-xs tracking-widest uppercase text-neutral-500">Loading…</div>;
  }

  const images = product.images && product.images.length ? product.images : [FALLBACK_IMG];
  const inStock = product.stock_status === 'in_stock';
  const qtyAvailable = Number(product.stock_qty || 0);

  return (
    <div className="container-x py-6 md:py-12" data-testid={IDS.product.root}>
      <Link to="/products" className="sans text-xs tracking-widest uppercase text-neutral-500 hover:text-black inline-flex items-center gap-2 mb-4 md:mb-6">
        <ArrowLeft size={14} /> Back to catalog
      </Link>

      <div className="grid gap-6 md:gap-10 lg:grid-cols-2">
        {/* Gallery + optional video */}
        <div data-testid={IDS.product.gallery}>
          {product.video_url && showVideo ? (
            <div className="aspect-square bg-black overflow-hidden border border-neutral-200 relative">
              <video
                src={product.video_url}
                controls
                autoPlay
                playsInline
                className="w-full h-full object-contain"
                data-testid="product-video"
              />
              <button
                type="button"
                onClick={() => setShowVideo(false)}
                className="absolute top-3 right-3 bg-white/90 px-3 py-1 sans text-xs tracking-widest uppercase"
                data-testid="product-video-close"
              >
                Close
              </button>
            </div>
          ) : (
            <div className="aspect-square bg-white overflow-hidden border border-neutral-200 relative">
              <img src={images[activeImage]} alt={product.name} className="w-full h-full object-contain" referrerPolicy="no-referrer" />
              {product.video_url && (
                <button
                  type="button"
                  onClick={() => setShowVideo(true)}
                  className="absolute bottom-3 right-3 flex items-center gap-2 bg-black/85 text-white px-3 py-2 sans text-xs tracking-widest uppercase hover:bg-black transition-colors"
                  data-testid="product-video-open"
                >
                  <PlayCircle size={14} /> Watch Video
                </button>
              )}
            </div>
          )}
          {images.length > 1 && (
            <div className="mt-4 grid grid-cols-5 gap-2">
              {images.map((src, i) => (
                <button
                  type="button"
                  key={i}
                  onClick={() => { setActiveImage(i); setShowVideo(false); }}
                  className={`aspect-square border overflow-hidden ${activeImage === i && !showVideo ? 'border-black' : 'border-neutral-200'}`}
                  data-testid={`product-thumb-${i}`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                </button>
              ))}
            </div>
          )}
          {(product.images && product.images.length > 0) && (
            <button
              type="button"
              onClick={downloadImagesZip}
              className="btn-outline w-full mt-4 justify-center"
              data-testid="pdp-download-images"
            >
              <ImageDown size={14} /> Download all images (ZIP)
            </button>
          )}
          <ProductDownloadsCard documents={product.documents} />
        </div>

        {/* Info */}
        <div>
          <p className="eyebrow" data-testid={IDS.product.brand}>{product.brand}</p>
          <h1 className="h-display text-2xl md:text-4xl mt-2 md:mt-3" data-testid={IDS.product.title}>{product.name}</h1>
          <p className="sans text-xs tracking-widest uppercase text-neutral-500 mt-2" data-testid={IDS.product.sku}>
            SKU {product.sku}{product.upc ? ` · UPC ${product.upc}` : ''}{product.variant ? ` · ${product.variant}` : ''}
          </p>

          {/* Pricing */}
          <div className="mt-5 md:mt-6 py-5 border-y border-neutral-200" data-testid={IDS.product.priceDisplay}>
            <PriceBlock product={product} size="lg" />
            <p className="sans text-[11px] text-neutral-500 mt-3 tracking-[0.18em] uppercase">
              Case of {product.case_pack_size} · MOQ {product.min_order_qty}
              {inStock && qtyAvailable > 0 && (
                <span className="ml-2" style={{ color: 'var(--ije-pink-dark)' }}>· {qtyAvailable.toLocaleString()} available</span>
              )}
            </p>
          </div>

          {/* Qty + add or Notify */}
          {!product.pricing_locked && inStock && (
            <div className="mt-5 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
              <div className="flex items-center border border-neutral-200">
                <button type="button" onClick={dec} data-testid={IDS.product.qtyDecrement} className="p-3 hover:bg-neutral-100 transition-colors">
                  <Minus size={16} />
                </button>
                <input
                  data-testid={IDS.product.qtyInput}
                  value={qty}
                  onChange={(e) => setQtyManual(e.target.value)}
                  className="w-16 text-center sans text-base border-x border-neutral-200 py-3 focus:outline-none"
                />
                <button type="button" onClick={inc} data-testid={IDS.product.qtyIncrement} className="p-3 hover:bg-neutral-100 transition-colors">
                  <Plus size={16} />
                </button>
              </div>
              <button type="button" onClick={addToCart} disabled={adding} className="btn-primary flex-1" data-testid={IDS.product.addToCart}>
                <ShoppingBag size={16} /> {adding ? 'Adding…' : 'Add to Cart'}
              </button>
            </div>
          )}
          {!product.pricing_locked && (
            <div className="mt-3">
              <AddToCatalogButton sku={product.sku} />
            </div>
          )}
          {!product.pricing_locked && !inStock && (
            <div className="mt-5">
              <NotifyForm product={product} />
            </div>
          )}
        </div>
      </div>

      <ProductInfoTabs product={product} />
    </div>
  );
}
